<script lang="ts" setup>
  import { onBeforeMount } from '#imports'
  import useAria from '~/composables/Aria/Aria'
  import useModelProvider, {
    UseModelProviderEmits,
    UseModelProviderProps,
  } from '~/composables/ModelProvider/ModelProvider'
  import { UiFormFieldFacet } from '~/components/Ui/UiFormFieldFacet'

  type ModelType = string

  interface Props extends UseModelProviderProps<ModelType> {
    title?: string
    items: Array<{ id: string; label: string }>
    modelValue: ModelType
  }

  interface Emits extends UseModelProviderEmits {
    (e: 'update:modelValue'): void
  }

  const props = defineProps<Props>()
  const emit = defineEmits<Emits>()
  const { ariaUid } = useAria(onBeforeMount, 'radio-list')
  const model = useModelProvider<ModelType>(props, emit)

  const getUid = (idx?: number) => {
    return ariaUid(undefined, idx)
  }
</script>

<template>
  <div class="c-radio-list u-typography-root">
    <h3 v-if="title" class="u-reset c-radio-list__title">
      {{ title }}
    </h3>

    <ul class="c-radio-list__items u-reset">
      <li v-for="({ id, label }, idx) in items" :key="id" class="c-radio-list__item">
        <UiRadio
          :id="getUid(idx)"
          :key="getUid(idx)"
          v-model="model"
          :facets="[UiFormFieldFacet.Base, UiFormFieldFacet.Multiline]"
          :name="getUid()"
          :value="id"
        >
          <template #label>
            <span class="c-radio-list__label">
              {{ label }}
            </span>
          </template>
        </UiRadio>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
  @use '/assets/scss/base/typography/typography' as type;

  .c-radio-list__title {
    margin-bottom: 4px;
  }

  .c-radio-list__items {
    display: grid;
    row-gap: 12px;
  }

  .c-radio-list__label {
    @include type.copy-large;
  }
</style>
